import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";

import {
  AddTeamMembersDocument,
  CancelInviteDocument,
  InviteMembersDocument,
  type InviteUserInput as InviteUserItem,
  RemoveTeamMembersDocument,
  ResendInviteDocument,
  UpdateMemberDocument,
  UpdateMemberRolesDocument,
  type UpdateUserInput,
  type UpdateUserRolesInput,
  type InviteFailure,
} from "#app/lib/data";
import { removeFromConnection } from "#app/lib/data/connection-cache-utils";
import { useAnalytics, AnalyticsContext, SettingsEvent } from "#app/lib/analytics";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import type { Omittable } from "@salestrak/ui";

export type InviteMembersResult = Omittable<{
  succeededEmails: string[];
  /** Created users for successful invites — id + email, for follow-up
   *  mutations that target the new member (e.g. share grants). */
  succeededUsers: Array<{ userId: string; email: string }>;
  failures: InviteFailure[];
  error?: string;
}>;

export function useMemberMutations() {
  const { track } = useAnalytics();
  const [updateMemberMutation] = useMutation(UpdateMemberDocument);
  const [updateMemberRolesMutation] = useMutation(UpdateMemberRolesDocument);
  const [addTeamMembersMutation] = useMutation(AddTeamMembersDocument);
  const [removeTeamMembersMutation] = useMutation(RemoveTeamMembersDocument);
  const [resendInviteMutation] = useMutation(ResendInviteDocument);
  const [cancelInviteMutation] = useMutation(CancelInviteDocument, {
    update(cache, { data }, { variables }) {
      const result = data?.cancelInvite;
      const userId = variables?.id;
      if (!userId) return;
      if (result && result.__typename === "CancelInviteSuccess") {
        removeFromConnection(
          cache,
          "users",
          { __typename: "User", userId: String(userId) },
          { evict: true },
        );
      }
    },
  });
  // No cache insert on invite: `users` is cached under filter-scoped
  // variants (memberStatus pools, per-team teamMembers aliases, the members
  // table's per-group where clauses), and an unscoped insertIntoConnection
  // would land the invitee in lists whose filter they don't match.
  const [inviteMembersMutation] = useMutation(InviteMembersDocument);

  const updateMember = useCallback(
    async (userId: string, input: UpdateUserInput) => {
      try {
        const { data } = await updateMemberMutation({
          variables: { id: userId, input },
        });
        const result = data?.updateUser;
        if (result && "code" in result) {
          toastManager.add({ title: result.message ?? "Failed to update member", type: "error" });
          return null;
        }
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.Update, label: "member" });
        toastManager.add({ title: "Member updated", type: "success" });
        return result ?? null;
      } catch (err) {
        toastMutationError(err, "Failed to update member");
        return null;
      }
    },
    [updateMemberMutation, track]
  );

  const updateMemberRoles = useCallback(
    async (userId: string, input: UpdateUserRolesInput) => {
      try {
        const { data } = await updateMemberRolesMutation({
          variables: { id: userId, input },
        });
        const result = data?.updateUserRoles;
        if (result && "code" in result) {
          toastManager.add({ title: result.message ?? "Failed to update member roles", type: "error" });
          return null;
        }
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.UpdateRoles, label: "member", data: { added: input.add, removed: input.remove } });
        toastManager.add({ title: "Member roles updated", type: "success" });
        return result ?? null;
      } catch (err) {
        toastMutationError(err, "Failed to update member roles");
        return null;
      }
    },
    [updateMemberRolesMutation, track]
  );

  const updateMemberTeams = useCallback(
    async (userId: string, currentTeamIds: string[], nextTeamIds: string[]) => {
      try {
        const currentSet = new Set(currentTeamIds);
        const nextSet = new Set(nextTeamIds);

        const toAdd = nextTeamIds.filter((id) => !currentSet.has(id));
        const toRemove = currentTeamIds.filter((id) => !nextSet.has(id));

        const results = await Promise.all([
          ...toAdd.map((teamId) =>
            addTeamMembersMutation({
              variables: { teamId, userIds: [userId], role: "member" },
            })
          ),
          ...toRemove.map((teamId) =>
            removeTeamMembersMutation({
              variables: { teamId, userIds: [userId] },
            })
          ),
        ]);
        // Show the first union-typed error if any leg failed.
        for (const r of results) {
          const payload =
            (r.data as any)?.addTeamMembers ?? (r.data as any)?.removeTeamMembers;
          if (payload && "code" in payload) {
            toastManager.add({ title: payload.message ?? "Failed to update member teams", type: "error" });
            return;
          }
        }
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.UpdateTeams, label: "member", data: { added: toAdd.length, removed: toRemove.length } });
        toastManager.add({ title: "Member teams updated", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to update member teams");
      }
    },
    [addTeamMembersMutation, removeTeamMembersMutation, track]
  );

  const resendInvite = useCallback(
    async (userId: string) => {
      try {
        const { data } = await resendInviteMutation({
          variables: { id: userId },
        });
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.ResendInvite, label: "member" });
        toastManager.add({ title: "Invite resent", type: "success" });
        return data?.resendInvite ?? null;
      } catch (err) {
        toastMutationError(err, "Failed to resend invite");
        return null;
      }
    },
    [resendInviteMutation, track]
  );

  const cancelInvite = useCallback(
    async (userId: string) => {
      try {
        const { data } = await cancelInviteMutation({
          variables: { id: userId },
        });
        const result = data?.cancelInvite;
        if (result && result.__typename === "ValidationError") {
          toastManager.add({ title: result.message ?? "Failed to cancel invite", type: "error" });
          return null;
        }
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.CancelInvite, label: "member" });
        toastManager.add({ title: "Invite cancelled", type: "success" });
        return result ?? null;
      } catch (err) {
        toastMutationError(err, "Failed to cancel invite");
        return null;
      }
    },
    [cancelInviteMutation, track]
  );

  const inviteMembers = useCallback(
    async (invites: InviteUserItem[]): Promise<InviteMembersResult> => {
      try {
        const { data } = await inviteMembersMutation({
          variables: { input: { invites } },
        });
        const result = data?.inviteUsers;
        if (!result) {
          const message = "Failed to invite members";
          toastManager.add({ title: message, type: "error" });
          return {
            succeededEmails: [],
            succeededUsers: [],
            failures: [],
            error: message,
          };
        }
        if (
          result.__typename === "ValidationError" ||
          result.__typename === "PermissionDenied"
        ) {
          const fallback =
            result.__typename === "ValidationError"
              ? "Invalid invite"
              : "Permission denied";
          const message = result.message ?? fallback;
          toastManager.add({ title: message, type: "error" });
          return {
            succeededEmails: [],
            succeededUsers: [],
            failures: [],
            error: message,
          };
        }
        const succeededUsers = result.users.flatMap((u) =>
          u.email ? [{ userId: u.userId, email: u.email }] : [],
        );
        const succeededEmails = succeededUsers.map((u) => u.email);
        const failures = result.failed.map((f) => ({ email: f.email, reason: f.reason }));
        if (succeededEmails.length > 0) {
          track({ context: AnalyticsContext.Settings, event: SettingsEvent.Invite, label: "member", data: { count: succeededEmails.length, failed: failures.length } });
          toastManager.add({
            title:
              succeededEmails.length === 1
                ? "Invite sent"
                : `${succeededEmails.length} invites sent`,
            type: "success",
          });
        }
        for (const f of failures) {
          toastManager.add({ title: `${f.email}: ${f.reason}`, type: "error" });
        }
        return { succeededEmails, succeededUsers, failures };
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to invite members";
        toastManager.add({ title: message, type: "error" });
        return {
          succeededEmails: [],
          succeededUsers: [],
          failures: [],
          error: message,
        };
      }
    },
    [inviteMembersMutation, track]
  );

  return { updateMember, updateMemberRoles, updateMemberTeams, resendInvite, cancelInvite, inviteMembers };
}
